import React from 'react';
import './style.scss';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { I18n } from 'react-redux-i18n';
import {
	Button,
	IconButton,
	Input,
	InputAdornment,
	MenuItem,
	Select,
	Switch,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';
import ClearIcon from '@material-ui/icons/Clear';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Alert from '../../../../Components/Alert';
import * as timeslotActions from '../../../../service/timeslot/actions';
import { confirm, deepEqual, isEmpty } from '../../../../utils';
import { getFlatProducts } from '../TimeslotBinding/selector';

// 1 = Lundi ... 7 = Dimanche
const DAYS = [
	{ value: 1, key: 'general.monday' },
	{ value: 2, key: 'general.tuesday' },
	{ value: 3, key: 'general.wednesday' },
	{ value: 4, key: 'general.thursday' },
	{ value: 5, key: 'general.friday' },
	{ value: 6, key: 'general.saturday' },
	{ value: 7, key: 'general.sunday' },
];

// comparaison tolérante number/string (l'id peut arriver en string depuis le parent)
const sameId = (a, b) => a != null && b != null && String(a) === String(b);

// Formulaire inline de gestion des créneaux midi/soir.
// La donnée est globale (state.service.timeslot) : le formulaire est affiché
// directement dans l'onglet « Créneaux » de la page de réglages (Containers/Settings).
class TimeslotManager extends React.Component {
	constructor(props) {
		super(props);
		// draft local : les modifications ne touchent le store qu'à l'enregistrement,
		// pour qu'un « Annuler » reparte de l'état persisté.
		this.state = { ...this.buildDraftFromProps(), openRecapId: null };
	}

	componentDidUpdate(prevProps) {
		// resynchronise le draft quand le store change (chargement initial, ou
		// après un enregistrement). En édition, le store ne bouge pas donc les
		// saisies ne sont pas écrasées.
		if (
			prevProps.enabled !== this.props.enabled ||
			prevProps.timeslotPos !== this.props.timeslotPos ||
			!deepEqual(prevProps.timeslots, this.props.timeslots)
		) {
			this.setState(this.buildDraftFromProps());
		}
	}

	buildDraftFromProps = () => ({
		enabled: this.props.enabled,
		timeslotPos: this.props.timeslotPos,
		// clone profond des timeslots pour ne pas muter le store
		timeslots: this.props.timeslots.map(ts => ({ ...ts })),
	});

	onToggleEnabled = e => {
		this.setState({ enabled: e.target.checked });
	};

	onToggleTimeslotPos = e => {
		this.setState({ timeslotPos: e.target.checked });
	};

	editTimeslot = (id, field, value) => {
		this.setState(prevState => ({
			timeslots: prevState.timeslots.map(ts =>
				sameId(ts.id, id) ? { ...ts, [field]: value } : ts
			),
		}));
	};

	onEditName = (id, value) => {
		this.editTimeslot(id, 'name', value);
	};

	onEditDay = (id, value) => {
		this.editTimeslot(id, 'day', Number(value));
	};

	onEditHour = (id, value) => {
		// number libre borné entre 0 et 23
		let hour = parseInt(value, 10);
		if (isNaN(hour)) hour = 0;
		hour = Math.max(0, Math.min(23, hour));
		this.editTimeslot(id, 'hour', hour);
	};

	onAdd = () => {
		this.setState(prevState => {
			// id temporaire (unique, remplacé par le back à la sauvegarde)
			const maxId = prevState.timeslots.reduce(
				(m, ts) => Math.max(m, ts.id),
				0
			);
			return {
				// ajouter un créneau réactive automatiquement les créneaux
				enabled: true,
				timeslots: [
					...prevState.timeslots,
					{ id: maxId + 1, name: '', day: 1, hour: 0, minute: 0 },
				],
			};
		});
	};

	// Génère un créneau midi (10h) et un créneau soir (17h) pour chaque jour.
	// Proposé uniquement quand aucun créneau n'existe encore (cf. render).
	onAutoCreate = () => {
		const middayName = I18n.t(
			'partner.export.timeslot_manager.auto_midday_name'
		);
		const eveningName = I18n.t(
			'partner.export.timeslot_manager.auto_evening_name'
		);
		let id = 1;
		const timeslots = [];
		DAYS.forEach(d => {
			timeslots.push({
				id: id++,
				name: middayName,
				day: d.value,
				hour: 10,
				minute: 0,
			});
			timeslots.push({
				id: id++,
				name: eveningName,
				day: d.value,
				hour: 17,
				minute: 0,
			});
		});
		// la génération crée des créneaux : on réactive automatiquement
		this.setState({ enabled: true, timeslots });
	};

	onDelete = id => {
		confirm(I18n.t('partner.export.timeslot_manager.delete_confirm')).then(
			() =>
				this.setState(prevState => {
					const timeslots = prevState.timeslots.filter(
						ts => !sameId(ts.id, id)
					);
					// plus aucun créneau : on désactive automatiquement
					return timeslots.length
						? { timeslots }
						: { timeslots, enabled: false };
				}),
			() => {}
		);
	};

	// Renvoie l'ensemble des ids de créneaux dont le nom est vide (ou blanc).
	// Un nom vide rend la sauvegarde impossible côté UI.
	getInvalidNameIds = () => {
		const invalid = new Set();
		this.state.timeslots.forEach(ts => {
			if (isEmpty(ts.name)) invalid.add(ts.id);
		});
		return invalid;
	};

	// Renvoie l'ensemble des ids de créneaux en doublon (même jour + même heure).
	// Un doublon rend la sauvegarde impossible côté UI.
	getDuplicateIds = () => {
		const seen = {};
		const duplicates = new Set();
		this.state.timeslots.forEach(ts => {
			const key = `${ts.day}-${ts.hour}`;
			if (seen[key] != null) {
				duplicates.add(ts.id);
				duplicates.add(seen[key]);
			} else {
				seen[key] = ts.id;
			}
		});
		return duplicates;
	};

	onSave = () => {
		// garde-fou : le bouton est déjà désactivé en cas de doublon / nom vide, mais on
		// double la sécurité pour ne jamais persister deux créneaux identiques ou sans nom.
		if (this.getDuplicateIds().size > 0 || this.getInvalidNameIds().size > 0)
			return;

		this.props.actions.saveTimeslots({
			enabled: this.state.enabled,
			timeslotPos: this.state.timeslotPos,
			timeslots: this.state.timeslots.map(ts => ({
				...ts,
				hour: parseInt(ts.hour, 10) || 0,
			})),
		});
	};

	hasChanges = () =>
		!deepEqual(
			{
				enabled: this.state.enabled,
				timeslotPos: this.state.timeslotPos,
				timeslots: this.state.timeslots,
			},
			{
				enabled: this.props.enabled,
				timeslotPos: this.props.timeslotPos,
				timeslots: this.props.timeslots,
			}
		);

	toggleRecap = id => {
		this.setState(prevState => ({
			openRecapId: sameId(prevState.openRecapId, id) ? null : id,
		}));
	};

	getRulesForTimeslot = timeslotId => {
		const { rules, flatProducts } = this.props;
		return rules
			.filter(
				rule => rule.idProduct != null && sameId(rule.idTimeslot, timeslotId)
			)
			.map(rule => {
				const product = flatProducts[String(rule.idProduct)];
				return {
					idProduct: rule.idProduct,
					name: product ? product.name : `#${rule.idProduct}`,
					rule: rule.rule || 'enable',
				};
			})
			// désactivés en premier (ce sont les exceptions), puis tri alphabétique
			.sort((a, b) => {
				if (a.rule !== b.rule) return a.rule === 'disable' ? -1 : 1;
				return (a.name || '').localeCompare(b.name || '');
			});
	};

	onRemoveRule = (timeslotId, idProduct, productName) => {
		confirm(
			I18n.t('partner.export.timeslot_manager.rule_remove_confirm', {
				product: productName,
			})
		).then(() => {
			const remaining = this.props.rules
				.filter(
					rule =>
						sameId(rule.idProduct, idProduct) &&
						!sameId(rule.idTimeslot, timeslotId)
				)
				.map(rule => ({ idTimeslot: rule.idTimeslot, rule: rule.rule }));
			this.props.actions.saveTimeslotRules({
				type: 'product',
				id: idProduct,
				rules: remaining,
			});
		}, () => {});
	};

	// bouton d'enregistrement réutilisé (haut + bas), même handler/état
	renderSaveButton = disabled => (
		<Button
			onClick={this.onSave}
			color="primary"
			variant="contained"
			disabled={disabled}
			className={'button-valid'}>
			{I18n.t('general.save')}
		</Button>
	);

	render() {
		const { enabled, timeslotPos, timeslots, openRecapId } = this.state;
		const duplicateIds = this.getDuplicateIds();
		const hasDuplicates = duplicateIds.size > 0;
		const invalidNameIds = this.getInvalidNameIds();
		const hasInvalidNames = invalidNameIds.size > 0;
		const saveDisabled =
			hasDuplicates || hasInvalidNames || !this.hasChanges();
		// valeur par défaut « activé » : seul un false explicite désactive.
		const posChecked = timeslotPos !== false;

		return (
			<div className="timeslot-manager">
				<div className="timeslot-manager-topbar">
					{this.renderSaveButton(saveDisabled)}
				</div>

				<Alert type="info">
					{I18n.t('partner.export.timeslot_manager.help')}
				</Alert>

				<div className="timeslot-manager-switches">
					<div className="timeslot-manager-enabled">
						<Switch
							checked={!!enabled}
							onChange={this.onToggleEnabled}
							color="primary"
							disabled={!timeslots.length}
						/>
						<span>{I18n.t('partner.export.timeslot_manager.enabled')}</span>
					</div>

					{/* la synchro caisse n'a de sens que si les créneaux sont activés */}
					{enabled && timeslots.length ? (
						<div className="timeslot-manager-enabled">
							<Switch
								checked={posChecked}
								onChange={this.onToggleTimeslotPos}
								color="primary"
							/>

							<span>{I18n.t('partner.export.timeslot_manager.pos_sync')}</span>
						</div>
					) : null}
				</div>

				<div className="timeslot-manager-list">
					{timeslots.length ? (
						<div className="timeslot-manager-header">
							<span className="timeslot-manager-name">
								{I18n.t('partner.export.timeslot_manager.name')}
							</span>
							<span className="timeslot-manager-day">
								{I18n.t('partner.export.timeslot_manager.day')}
							</span>
							<span className="timeslot-manager-hour">
								{I18n.t('partner.export.timeslot_manager.hour')}
							</span>
							<span className="timeslot-manager-actions" />
						</div>
					) : null}
					{!timeslots.length ? (
						<p className="timeslot-manager-empty">
							{I18n.t('partner.export.timeslot_manager.empty_placeholder')}
						</p>
					) : null}
					{timeslots.map(ts => {
						const ruleRows = this.getRulesForTimeslot(ts.id);
						const recapOpen = sameId(openRecapId, ts.id);
						return (
							<div key={ts.id} className="timeslot-manager-item">
								<div
									className={
										'timeslot-manager-row' +
										(duplicateIds.has(ts.id)
											? ' timeslot-manager-row-duplicate'
											: '')
									}>
									<Input
										className={
											'timeslot-manager-name' +
											(invalidNameIds.has(ts.id)
												? ' timeslot-manager-name-error'
												: '')
										}
										error={invalidNameIds.has(ts.id)}
										placeholder={I18n.t(
											'partner.export.timeslot_manager.name'
										)}
										value={ts.name || ''}
										onChange={e => this.onEditName(ts.id, e.target.value)}
									/>

									<Select
										className="timeslot-manager-day"
										value={ts.day}
										onChange={e => this.onEditDay(ts.id, e.target.value)}>
										{DAYS.map(d => (
											<MenuItem key={d.value} value={d.value}>
												{I18n.t(d.key)}
											</MenuItem>
										))}
									</Select>

									<Input
										className="timeslot-manager-hour"
										type="number"
										inputProps={{ min: 0, max: 23 }}
										endAdornment={
											<InputAdornment position="end">h</InputAdornment>
										}
										value={ts.hour}
										onChange={e => this.onEditHour(ts.id, e.target.value)}
									/>

									<span className="timeslot-manager-actions">
										<IconButton
											size="small"
											onClick={() => this.onDelete(ts.id)}>
											<DeleteIcon />
										</IconButton>
									</span>

									<button
										type="button"
										className={
											'timeslot-manager-recap-toggle' +
											(recapOpen ? ' is-open' : '')
										}
										onClick={() => this.toggleRecap(ts.id)}>
										<span>
											{I18n.t(
												'partner.export.timeslot_manager.recap_link',
												{ count: ruleRows.length }
											)}
										</span>
										<ExpandMoreIcon fontSize="small" />
									</button>
								</div>

								{recapOpen ? (
									<div className="timeslot-manager-recap">
										{ruleRows.length ? (
											ruleRows.map(r => (
												<div
													key={r.idProduct}
													className={
														'timeslot-manager-recap-row timeslot-manager-recap-row-' +
														r.rule
													}>
													<span className="timeslot-manager-recap-name">
														{r.name}
													</span>
													<span
														className={
															'timeslot-manager-recap-state timeslot-manager-recap-state-' +
															r.rule
														}>
														{I18n.t(
															r.rule === 'disable'
																? 'partner.export.timeslot_manager.recap_becomes_unavailable'
																: 'partner.export.timeslot_manager.recap_becomes_available'
														)}
													</span>
													<IconButton
														size="small"
														onClick={() =>
															this.onRemoveRule(ts.id, r.idProduct, r.name)
														}>
														<ClearIcon fontSize="small" />
													</IconButton>
												</div>
											))
										) : (
											<p className="timeslot-manager-recap-empty">
												{I18n.t('partner.export.timeslot_manager.recap_none')}
											</p>
										)}
									</div>
								) : null}
							</div>
						);
					})}
				</div>

				<Button
					className="timeslot-manager-add"
					startIcon={<AddIcon />}
					color="secondary"
					onClick={this.onAdd}>
					{I18n.t('partner.export.timeslot_manager.add')}
				</Button>

				{/* génération automatique : proposée seulement tant qu'aucun
				    créneau n'existe, pour ne pas écraser une config existante */}
				{!timeslots.length ? (
					<Button
						className="timeslot-manager-auto-create"
						color="secondary"
						variant="contained"
						onClick={this.onAutoCreate}>
						{I18n.t('partner.export.timeslot_manager.auto_create')}
					</Button>
				) : null}

				{hasInvalidNames ? (
					<p className="timeslot-manager-duplicate-error">
						{I18n.t('partner.export.timeslot_manager.name_error')}
					</p>
				) : null}

				{hasDuplicates ? (
					<p className="timeslot-manager-duplicate-error">
						{I18n.t('partner.export.timeslot_manager.duplicate_error')}
					</p>
				) : null}

				<div className="timeslot-manager-footer">
					{this.renderSaveButton(saveDisabled)}
				</div>
			</div>
		);
	}
}

const mapStateToProps = state => {
	const timeslot = (state.service && state.service.timeslot) || {};
	return {
		enabled: timeslot.enabled || false,
		timeslotPos: timeslot.timeslot_pos,
		timeslots: timeslot.timeslots || [],
		rules: timeslot.rules || [],
		flatProducts: getFlatProducts(state),
	};
};

const mapDispatchToProps = dispatch => ({
	actions: bindActionCreators(timeslotActions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(TimeslotManager);
